import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings, Check, Shield } from "lucide-react";

const STORAGE_KEY = "encantify_cookies_v1";

const defaultPrefs = {
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: null,
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...prefs, timestamp: new Date().toISOString() })
    );
  } catch {}
  applyPrefs(prefs);
}

// Apply consent to GA (consent mode v2) and any other trackers
function applyPrefs(prefs) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  const consent = {
    ad_storage: prefs.marketing ? "granted" : "denied",
    ad_user_data: prefs.marketing ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
    analytics_storage: prefs.analytics ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  };
  // gtag may or may not exist depending on whether GA is loaded
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", consent);
  } else {
    window.dataLayer.push(["consent", "update", consent]);
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    const stored = loadPrefs();
    if (stored) {
      setPrefs(stored);
      applyPrefs(stored);
    } else {
      setVisible(true);
    }
    const onReset = () => {
      setPrefs(defaultPrefs);
      setShowSettings(false);
      setVisible(true);
    };
    window.addEventListener("encantify-cookies-reset", onReset);
    return () => window.removeEventListener("encantify-cookies-reset", onReset);
  }, []);

  const acceptAll = useCallback(() => {
    const p = { necessary: true, analytics: true, marketing: true };
    setPrefs(p);
    savePrefs(p);
    setVisible(false);
  }, []);

  const rejectAll = useCallback(() => {
    const p = { necessary: true, analytics: false, marketing: false };
    setPrefs(p);
    savePrefs(p);
    setVisible(false);
  }, []);

  const saveCustom = useCallback(() => {
    savePrefs(prefs);
    setVisible(false);
    setShowSettings(false);
  }, [prefs]);

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="cookie-banner"
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ type: "spring", stiffness: 240, damping: 24 }}
        className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 z-[60] flex justify-center pointer-events-none"
        role="dialog"
        aria-label="Aviso de cookies"
        data-testid="cookie-banner"
      >
        <div className="w-full max-w-3xl pointer-events-auto rounded-3xl shadow-2xl bg-white border border-slate-200 p-5 md:p-6">
          {!showSettings ? (
            <div className="flex flex-col md:flex-row gap-4 md:items-center">
              <span className="w-11 h-11 rounded-2xl bg-[#FAD0C9]/30 inline-flex items-center justify-center shrink-0">
                <Cookie className="w-5 h-5 text-[#A66258]" strokeWidth={2.2} />
              </span>

              <div className="flex-1 min-w-0">
                <h2 className="font-display text-base md:text-lg font-semibold text-slate-900 leading-tight">
                  Cookies en Encantify
                </h2>
                <p className="text-[13px] md:text-sm text-slate-600 mt-1 leading-relaxed">
                  Usamos cookies propias y de terceros para analizar el tráfico y gestionar nuestro programa de afiliación oficial Disneyland Paris. Tu elección no afecta al precio que pagas. Más info en{" "}
                  <Link to="/cookies" className="font-semibold text-slate-900 underline">Política de Cookies</Link>.
                </p>
              </div>

              <div className="flex flex-wrap gap-2 md:flex-nowrap shrink-0">
                <button
                  type="button"
                  onClick={() => setShowSettings(true)}
                  className="text-xs md:text-sm font-semibold px-3.5 py-2 rounded-full bg-white text-slate-700 border border-slate-200 hover:border-slate-400 transition inline-flex items-center gap-1.5"
                  data-testid="cookie-settings-btn"
                >
                  <Settings className="w-3.5 h-3.5" /> Configurar
                </button>
                <button
                  type="button"
                  onClick={rejectAll}
                  className="text-xs md:text-sm font-semibold px-3.5 py-2 rounded-full bg-white text-slate-700 border border-slate-200 hover:border-slate-400 transition"
                  data-testid="cookie-reject-btn"
                >
                  Rechazar
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="text-xs md:text-sm font-semibold px-4 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition inline-flex items-center gap-1.5"
                  data-testid="cookie-accept-btn"
                >
                  <Check className="w-3.5 h-3.5" /> Aceptar todas
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-display text-lg md:text-xl font-semibold text-slate-900 leading-tight inline-flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#6B8CB3]" /> Configurar cookies
                  </h2>
                  <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">
                    Activa o desactiva las categorías según tus preferencias.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSettings(false)}
                  className="w-8 h-8 rounded-full inline-flex items-center justify-center text-slate-500 hover:bg-slate-100 transition"
                  aria-label="Cerrar"
                  data-testid="cookie-close-settings"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-4 space-y-3">
                <CookieRow
                  title="Técnicas (necesarias)"
                  desc="Imprescindibles para el funcionamiento del sitio. Siempre activas."
                  checked
                  disabled
                />
                <CookieRow
                  title="Analíticas (Google Analytics)"
                  desc="Nos ayudan a entender qué contenidos son más útiles. Datos agregados y anonimizados."
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                  testid="cookie-toggle-analytics"
                />
                <CookieRow
                  title="Marketing y afiliación (TradeDoubler / Amazon)"
                  desc="Permiten atribuir correctamente las reservas oficiales de Disneyland Paris. Sin coste para ti."
                  checked={prefs.marketing}
                  onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                  testid="cookie-toggle-marketing"
                />
              </div>

              <div className="mt-5 flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  onClick={rejectAll}
                  className="text-xs md:text-sm font-semibold px-3.5 py-2 rounded-full bg-white text-slate-700 border border-slate-200 hover:border-slate-400 transition"
                  data-testid="cookie-reject-from-settings"
                >
                  Rechazar todo
                </button>
                <button
                  type="button"
                  onClick={saveCustom}
                  className="text-xs md:text-sm font-semibold px-4 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition"
                  data-testid="cookie-save-btn"
                >
                  Guardar preferencias
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function CookieRow({ title, desc, checked, onChange, disabled, testid }) {
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-2xl border ${
        disabled ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200 hover:border-slate-300"
      } transition cursor-pointer`}
    >
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        <span className="block text-xs text-slate-600 mt-0.5 leading-relaxed">{desc}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange?.(!checked)}
        className={`relative w-11 h-6 rounded-full transition shrink-0 mt-0.5 ${
          checked ? "bg-slate-900" : "bg-slate-300"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        data-testid={testid}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </label>
  );
}
